import React, { useState, useEffect, useCallback, useRef } from "react";
import { useMsal } from "@azure/msal-react";
import { useNavigate } from "react-router-dom";
import { callApi } from "../api/api";
import {
  ArrowLeft,
  RefreshCw,
  GripVertical,
  ChevronUp,
  ChevronDown,
  ChevronRight,
  ChevronLeft,
  Shield,
  Building2,
  FileText,
  Save,
  RotateCcw,
  CheckCircle2,
  AlertCircle,
  Activity,
  Sparkles,
  GitMerge,
  Search,
  PlayCircle,
  Plus,
  ArrowRight,
  X
} from "lucide-react";

const THEME = "rgb(37, 99, 235)";

export default function RoleWorkflowOrder({ onBack }) {
  const navigate = useNavigate();
  const { instance, accounts } = useMsal();

  const [assignedRoles, setAssignedRoles] = useState([]);
  const [unassignedRoles, setUnassignedRoles] = useState([]);
  const [mappings, setMappings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const [selectedAvailableCode, setSelectedAvailableCode] = useState(null);
  const [selectedAssignedCode, setSelectedAssignedCode] = useState(null);

  const [leftSearchQuery, setLeftSearchQuery] = useState("");
  const [rightSearchQuery, setRightSearchQuery] = useState("");

  const [error, setError] = useState("");
  const [toast, setToast] = useState(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Drag & Drop State for Right Box
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);

  // Collapsible Mapped Document Types State (default hidden)
  const [expandedDocTypes, setExpandedDocTypes] = useState({});
  const toggleExpandDocs = (roleCode) => {
    setExpandedDocTypes((prev) => ({
      ...prev,
      [roleCode]: !prev[roleCode],
    }));
  };

  // Simulation State
  const [simulating, setSimulating] = useState(false);
  const [simStep, setSimStep] = useState(null);
  const [simLog, setSimLog] = useState("");
  const cardRefs = useRef({});
  const simIntervalRef = useRef(null);

  const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 1024px)");
    const update = (e) => setIsMobile(e.matches);
    setIsMobile(mq.matches);
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);

  const stopSimulation = useCallback((reason = "complete") => {
    if (simIntervalRef.current) {
      clearInterval(simIntervalRef.current);
      simIntervalRef.current = null;
    }
    setSimulating(false);
    setSimStep(null);
    setSimLog("");
    if (reason === "user_scroll") {
      showToast("⏸ Simulation stopped due to manual user scroll.", "info");
    }
  }, []);

  useEffect(() => {
    if (simulating && simStep !== null && cardRefs.current[simStep]) {
      cardRefs.current[simStep].scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  }, [simulating, simStep]);

  useEffect(() => {
    if (!simulating) return;

    const handleUserScroll = () => {
      stopSimulation("user_scroll");
    };

    window.addEventListener("wheel", handleUserScroll, { passive: true });
    window.addEventListener("touchmove", handleUserScroll, { passive: true });

    return () => {
      window.removeEventListener("wheel", handleUserScroll);
      window.removeEventListener("touchmove", handleUserScroll);
    };
  }, [simulating, stopSimulation]);

  const handleSimulateWorkflow = () => {
    if (assignedRoles.length === 0 || simulating) return;
    setSimulating(true);
    setSimStep(0);
    setSimLog(`📄 Document [CLAIMS_ENDORSEMENT.PDF] entering Priority Node #1...`);

    let current = 0;
    simIntervalRef.current = setInterval(() => {
      current += 1;
      if (current < assignedRoles.length) {
        setSimStep(current);
        setSimLog(`⚡ Fallback routing evaluated: Passed to Step ${current + 1} (${assignedRoles[current].roleName})`);
      } else {
        if (simIntervalRef.current) {
          clearInterval(simIntervalRef.current);
          simIntervalRef.current = null;
        }
        setTimeout(() => {
          setSimulating(false);
          setSimStep(null);
          setSimLog("");
          showToast("🎯 Routing Simulation Complete: 100% throughput across pipeline in 14ms!", "success");
        }, 1000);
      }
    }, 1000);
  };

  const handleAutoOptimizeOrder = () => {
    const sorted = [...assignedRoles].sort((a, b) => {
      const countA = (roleToMappingsMap[a.roleCode] || []).length;
      const countB = (roleToMappingsMap[b.roleCode] || []).length;
      if (countB !== countA) return countB - countA;
      return a.roleName.localeCompare(b.roleName);
    });

    const reordered = sorted.map((r, i) => ({
      ...r,
      priorityOrder: i + 1,
    }));

    setAssignedRoles(reordered);
    setHasUnsavedChanges(true);
    showToast("✨ Auto-Optimization Applied! High-volume roles prioritized.", "success");
  };

  const [initialLoading, setInitialLoading] = useState(true);

  /* =========================================
     FETCH DATA
  ========================================= */
  const fetchData = useCallback(async (isInitial = false) => {
    try {
      if (isInitial) {
        setInitialLoading(true);
      }
      setError("");

      const [ordersRes, mappingsRes] = await Promise.all([
        callApi(instance, accounts, "GET", `/api/assignments/workflow-orders?_t=${Date.now()}`),
        callApi(instance, accounts, "GET", `/api/assignments/role-mappings?_t=${Date.now()}`),
      ]);

      const loadedData = ordersRes?.data?.data || ordersRes?.data || {};
      const loadedMappings = mappingsRes?.data?.data || (Array.isArray(mappingsRes?.data) ? mappingsRes.data : []);

      let assigned = [];
      let unassigned = [];

      if (loadedData && Array.isArray(loadedData.assignedRoles)) {
        assigned = loadedData.assignedRoles;
        unassigned = loadedData.unassignedRoles || [];
      } else if (Array.isArray(loadedData)) {
        assigned = loadedData;
        unassigned = [];
      }

      const filteredAssigned = assigned.filter((r) => !r.isDeleted && r.isActive !== false && r.is_active !== 0);
      const filteredUnassigned = unassigned.filter((r) => !r.isDeleted && r.isActive !== false && r.is_active !== 0);
      setAssignedRoles(filteredAssigned.map((r, i) => ({ ...r, priorityOrder: i + 1 })));
      setUnassignedRoles(filteredUnassigned);
      setMappings(Array.isArray(loadedMappings) ? loadedMappings.filter((m) => !m.isDeleted && m.isActive !== false && m.is_active !== 0) : []);
      setHasUnsavedChanges(false);
    } catch (err) {
      console.error("Failed to fetch workflow priority orders:", err);
      setError(err?.response?.data?.message || err.message || "Failed to load workflow priority order.");
    } finally {
      setInitialLoading(false);
      setLoading(false);
      setRefreshing(false);
    }
  }, [instance, accounts]);

  useEffect(() => {
    fetchData(true);
  }, [fetchData]);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchData();
  };

  const showToast = (message, type = "success") => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 5000);
  };

  /* =========================================
     LOOKUP MAP FOR CONNECTED DOC TYPES PER ROLE
  ========================================= */
  const roleToMappingsMap = React.useMemo(() => {
    const map = {};
    mappings.forEach((m) => {
      if (m.roleCode) {
        if (!map[m.roleCode]) map[m.roleCode] = [];
        map[m.roleCode].push(m);
      }
    });
    return map;
  }, [mappings]);

  /* =========================================
     MOVE ROLES BETWEEN LEFT (POOL) & RIGHT (PIPELINE)
  ========================================= */
  const moveRoleToRight = (roleToMove) => {
    if (!roleToMove) return;

    // Return all currently assigned departments back to the unassigned pool
    const previousAssigned = [...assignedRoles];

    const updatedUnassigned = [
      ...unassignedRoles.filter((r) => r.roleCode !== roleToMove.roleCode),
      ...previousAssigned.map((r) => ({ ...r, priorityOrder: null })),
    ].sort((a, b) => (a.roleName || "").localeCompare(b.roleName || ""));

    // Set the selected department as the ONLY active department in the pipeline at Priority #1
    const updatedAssigned = [{ ...roleToMove, priorityOrder: 1 }];

    setUnassignedRoles(updatedUnassigned);
    setAssignedRoles(updatedAssigned);
    setSelectedAvailableCode(null);
    setHasUnsavedChanges(true);
  };

  const moveRoleToLeft = (roleToMove) => {
    if (!roleToMove) return;

    // Remove from assigned
    const updatedAssigned = assignedRoles
      .filter((r) => r.roleCode !== roleToMove.roleCode)
      .map((r, i) => ({ ...r, priorityOrder: i + 1 }));

    // Add to unassigned
    const updatedUnassigned = [...unassignedRoles, roleToMove];

    setAssignedRoles(updatedAssigned);
    setUnassignedRoles(updatedUnassigned);
    setSelectedAssignedCode(null);
    setHasUnsavedChanges(true);
  };

  /* =========================================
     MOVE CARD UP / DOWN HANDLERS IN PIPELINE
  ========================================= */
  const moveCard = (index, direction) => {
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= assignedRoles.length) return;

    const newRoles = [...assignedRoles];
    const temp = newRoles[index];
    newRoles[index] = newRoles[newIndex];
    newRoles[newIndex] = temp;

    const reordered = newRoles.map((r, i) => ({
      ...r,
      priorityOrder: i + 1,
    }));

    setAssignedRoles(reordered);
    setHasUnsavedChanges(true);
  };

  /* =========================================
     DRAG AND DROP HANDLERS (RIGHT BOX)
  ========================================= */
  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.setData("text/plain", index);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (dragOverIndex !== index) {
      setDragOverIndex(index);
    }
  };

  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    setDragOverIndex(null);

    const fromIndex = draggedIndex;
    if (fromIndex === null || fromIndex === dropIndex) return;

    const newRoles = [...assignedRoles];
    const [draggedItem] = newRoles.splice(fromIndex, 1);
    newRoles.splice(dropIndex, 0, draggedItem);

    const reordered = newRoles.map((r, i) => ({
      ...r,
      priorityOrder: i + 1,
    }));

    setAssignedRoles(reordered);
    setDraggedIndex(null);
    setHasUnsavedChanges(true);
  };

  /* =========================================
     SAVE PRIORITY ORDER TO BACKEND
  ========================================= */
  const handleSaveOrder = async () => {
    if (assignedRoles.length === 0) {
      showToast("⚠️ Active workflow pipeline is empty. Please assign at least one user role using ( > ) before saving.", "error");
      return;
    }

    try {
      setSaving(true);
      const roleOrderList = assignedRoles.map((r) => ({
        roleId: r.roleId,
        roleCode: r.roleCode,
        roleName: r.roleName,
      }));

      await callApi(instance, accounts, "PUT", "/api/assignments/workflow-orders", {
        roleOrderList,
      });

      showToast("⚡ Workflow role priority order saved successfully!", "success");
      setHasUnsavedChanges(false);
      fetchData();
    } catch (err) {
      console.error("Save workflow order failed:", err);
      showToast(err?.response?.data?.message || err.message || "Failed to save workflow order.", "error");
    } finally {
      setSaving(false);
    }
  };

  /* =========================================
     RESET ORDER
  ========================================= */
  const [pendingResetConfirm, setPendingResetConfirm] = useState(false);

  const handleResetOrder = () => {
    setPendingResetConfirm(true);
  };

  const confirmResetOrder = () => {
    const sorted = [...assignedRoles].sort((a, b) => a.roleName.localeCompare(b.roleName));
    const reordered = sorted.map((r, i) => ({
      ...r,
      priorityOrder: i + 1,
    }));
    setAssignedRoles(reordered);
    setHasUnsavedChanges(true);
    setPendingResetConfirm(false);
    showToast("Reset workflow priority order to default alphabetical.", "info");
  };

  /* =========================================
     FILTERED ROLES
  ========================================= */
  const filteredAvailable = unassignedRoles.filter((r) => {
    const q = leftSearchQuery.toLowerCase().trim();
    if (!q) return true;
    return (
      (r.roleName || "").toLowerCase().includes(q) ||
      (r.roleCode || "").toLowerCase().includes(q)
    );
  });

  const filteredAssigned = assignedRoles.filter((r) => {
    const q = rightSearchQuery.toLowerCase().trim();
    if (!q) return true;
    return (
      (r.roleName || "").toLowerCase().includes(q) ||
      (r.roleCode || "").toLowerCase().includes(q)
    );
  });

  return (
    <div style={s.page} className="smooth-fade-in">
      <style>{`
        ::-webkit-scrollbar {
          width: 6px;
          height: 6px;
        }
        ::-webkit-scrollbar-track {
          background: #f8fafc;
          border-radius: 4px;
        }
        ::-webkit-scrollbar-thumb {
          background: #cbd5e1;
          border-radius: 4px;
        }
        ::-webkit-scrollbar-thumb:hover {
          background: #94a3b8;
        }
        @keyframes spin-icon-anim {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @keyframes smoothFadeIn {
          from { opacity: 0.85; transform: scale(0.998); }
          to { opacity: 1; transform: scale(1); }
        }
        .spin-icon {
          animation: spin-icon-anim 0.75s linear infinite;
        }
        .smooth-fade-in {
          animation: smoothFadeIn 0.3s ease-out;
        }
        .transfer-btn:hover {
          background: #1d4ed8 !important;
          transform: scale(1.06);
          box-shadow: 0 6px 16px rgba(37,99,235,0.35) !important;
        }
        .transfer-btn:disabled {
          background: #e2e8f0 !important;
          color: #94a3b8 !important;
          cursor: not-allowed !important;
          transform: none !important;
          box-shadow: none !important;
        }
        .quick-add-btn:hover {
          background: #2563eb !important;
          color: #ffffff !important;
          border-color: #2563eb !important;
          box-shadow: 0 4px 10px rgba(37,99,235,0.25);
        }
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.4; }
        }
      `}</style>

      {/* TOAST NOTIFICATION */}
      {toast && (
        <div style={s.toastNotification(toast.type)}>
          {toast.type === "success" ? <CheckCircle2 size={16} /> : <AlertCircle size={16} />}
          <span>{toast.message}</span>
        </div>
      )}

      {/* HEADER */}
      <div style={s.header}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "6px", flexWrap: "wrap", gap: "12px" }}>
          <div>
            <div style={s.breadcrumb}>
              <span>Administration</span> &gt;{" "}
              <span
                onClick={onBack || (() => navigate("/manage"))}
                style={{ color: "#2563eb", fontWeight: "600", cursor: "pointer" }}
                onMouseEnter={(e) => { e.currentTarget.style.textDecoration = "underline"; }}
                onMouseLeave={(e) => { e.currentTarget.style.textDecoration = "none"; }}
                title="Click to go to Manage Menu"
              >
                Manage
              </span>{" "}
              &gt; <span style={{ color: "#0f172a", fontWeight: "600" }}>Manage Workflows</span>
            </div>
            <h1 style={s.title}>Manage Workflows &amp; Priority Order</h1>
            <p style={s.sub}>
              Assign and manage priority department order for the workflow ingestion pipeline.
            </p>
          </div>

          <div style={s.headerActions}>
            {hasUnsavedChanges && (
              <span style={s.unsavedBadge}>● Unsaved Changes</span>
            )}
            <button
              style={s.saveBtn(hasUnsavedChanges && !saving)}
              disabled={!hasUnsavedChanges || saving}
              onClick={handleSaveOrder}
            >
              <Save size={14} />
              {saving ? "Saving Order..." : "Save Workflow Order"}
            </button>

            <button style={s.refreshBtn} onClick={handleRefresh} disabled={refreshing}>
              <RefreshCw size={14} className={refreshing ? "spin-icon" : ""} />
              {refreshing ? "Refreshing..." : "Refresh"}
            </button>
          </div>
        </div>
      </div>

      {/* TELEMETRY CARDS STRIP */}
      {/* <div style={s.statsGrid}>
        <div style={s.statCard}>
          <div style={s.statIconBox("#3b82f6")}>
            <Shield size={18} color="#3b82f6" />
          </div>
          <div>
            <div style={s.statValue}>{assignedRoles.length + unassignedRoles.length}</div>
            <div style={s.statLabel}>Total Workflow Roles</div>
          </div>
        </div>

        <div style={s.statCard}>
          <div style={s.statIconBox("#8b5cf6")}>
            <Sparkles size={18} color="#8b5cf6" />
          </div>
          <div>
            <div style={s.statValue}>
              {assignedRoles[0] ? assignedRoles[0].roleName : "None"}
            </div>
            <div style={s.statLabel}>#1 Priority Role</div>
          </div>
        </div>

        <div style={s.statCard}>
          <div style={s.statIconBox("#10b981")}>
            <FileText size={18} color="#10b981" />
          </div>
          <div>
            <div style={s.statValue}>{mappings.length}</div>
            <div style={s.statLabel}>Connected Doc Types</div>
          </div>
        </div>

        <div style={s.statCard}>
          <div style={s.statIconBox("#0d9488")}>
            <Activity size={18} color="#0d9488" />
          </div>
          <div>
            <div style={s.statValue}>Sequential</div>
            <div style={s.statLabel}>Routing Strategy</div>
          </div>
        </div>
      </div> */}

      {/* LIVE SIMULATION TELEMETRY BANNER */}
      {simulating && (
        <div style={s.simBanner}>
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <Activity size={18} color="#2563eb" className="spin-icon" />
            <div>
              <div style={{ fontSize: "13px", fontWeight: "700", color: "#1e40af" }}>
                ⚡ LIVE ROUTING ENGINE SIMULATION IN PROGRESS
              </div>
              <div style={{ fontSize: "11px", color: "#3b82f6", marginTop: "2px" }}>
                {simLog}
              </div>
            </div>
          </div>
          <div style={{ fontSize: "11px", fontWeight: "600", color: "#1e3a8a", background: "#dbeafe", padding: "4px 10px", borderRadius: "12px" }}>
            Latency: 12ms | SLA: 100%
          </div>
        </div>
      )}

      {/* ERROR ALERT */}
      {error && (
        <div style={s.errorAlert}>
          <AlertCircle size={16} color="#dc2626" />
          <span>{error}</span>
        </div>
      )}

      {/* DUAL BOX WORKFLOW TRANSFER LIST CONTAINER */}
      <div
        className="dual-box-grid"
        style={{
          display: "grid",
          gridTemplateColumns: isMobile ? "1fr" : "1fr 50px 1.25fr",
          gap: "16px",
          alignItems: "start",
          width: "100%",
        }}
      >

        {/* LEFT BOX: AVAILABLE UNASSIGNED ROLES POOL */}
        <div style={s.panelCard}>
          {refreshing && (
            <div style={s.overlayLoader}>
              <div style={s.overlayLoaderPill}>
                <RefreshCw size={16} color="#2563eb" className="spin-icon" />
                <span>Updating roles…</span>
              </div>
            </div>
          )}

          <div style={s.panelHeader}>
            <div>
              <div style={s.panelTitle}>Available Departments</div>
              <div style={s.panelSubtitle}>Select departments to add into the ingestion pipeline</div>
            </div>
            <span style={s.panelCountBadge}>{filteredAvailable.length} Available</span>
          </div>

          <div style={s.panelSearchBox}>
            <Search size={14} color="#94a3b8" />
            <input
              type="text"
              placeholder="Search available departments..."
              value={leftSearchQuery}
              onChange={(e) => setLeftSearchQuery(e.target.value)}
              style={s.panelSearchInput}
            />
          </div>

          <div style={s.panelBodyScroll}>
            {loading ? (
              Array.from({ length: 5 }).map((_, i) => (
                <div key={`left-skel-${i}`} style={{ ...s.availableRoleItem(false), opacity: 0.8 }}>
                  <div style={s.availRoleIconBox}><div style={{ ...s.skeletonBar, width: "18px", height: "18px", borderRadius: "4px" }} /></div>
                  <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: "6px" }}>
                    <div style={{ ...s.skeletonBar, width: "140px", height: "14px" }} />
                    <div style={{ ...s.skeletonBar, width: "60px", height: "10px" }} />
                  </div>
                  <div style={{ ...s.skeletonBar, width: "50px", height: "26px", borderRadius: "6px" }} />
                </div>
              ))
            ) : filteredAvailable.length === 0 ? (
              <div style={s.emptyStateBox}>
                <Building2 size={24} color="#94a3b8" />
                <div style={{ fontSize: "13px", fontWeight: "600", color: "#475569", marginTop: "6px" }}>
                  {leftSearchQuery ? "No matching departments found" : "All departments assigned to pipeline!"}
                </div>
                <div style={{ fontSize: "11.5px", color: "#94a3b8", marginTop: "2px" }}>
                  {leftSearchQuery ? "Try searching another term" : "Your active priority pipeline contains all departments."}
                </div>
              </div>
            ) : (
              filteredAvailable.map((role) => {
                const roleMappings = roleToMappingsMap[role.roleCode] || [];
                const isSelected = selectedAvailableCode === role.roleCode;

                return (
                  <div
                    key={role.roleCode}
                    onClick={() => setSelectedAvailableCode(role.roleCode)}
                    style={s.availableRoleItem(isSelected)}
                  >
                    <div style={s.availRoleIconBox}>
                      <Building2 size={18} color="#2563eb" />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: "14px", fontWeight: "700", color: "#0f172a", lineHeight: 1.3 }}>
                        {role.roleName}
                      </div>
                      <div style={s.roleCodeBadge}>{role.roleCode}</div>
                      <div style={{ fontSize: "11px", color: "#64748b", marginTop: "4px" }}>
                        Mapped Doc Types: <strong>{roleMappings.length}</strong>
                      </div>
                    </div>
                    <button
                      type="button"
                      className="quick-add-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        moveRoleToRight(role);
                      }}
                      style={{
                        ...s.quickAddBtn,
                        background: assignedRoles.length > 0 ? "#eff6ff" : "#ffffff",
                        color: assignedRoles.length > 0 ? "#1d4ed8" : "#2563eb",
                        borderColor: assignedRoles.length > 0 ? "#bfdbfe" : "#cbd5e1",
                      }}
                      title={assignedRoles.length > 0 ? "Replace Active Pipeline Department" : "Add to Active Pipeline"}
                    >
                      {assignedRoles.length > 0 ? <RefreshCw size={12} /> : <Plus size={13} />}
                      <span>{assignedRoles.length > 0 ? "Replace" : "Add"}</span>
                      {isMobile ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
                    </button>
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* CENTER TRANSFER CONTROLS COLUMN ( > and < ) */}
        <div
          className="transfer-column"
          style={{
            display: "flex",
            flexDirection: isMobile ? "row" : "column",
            alignItems: "center",
            justifyContent: "center",
            alignSelf: "center",
            gap: "14px",
            margin: isMobile ? "12px 0" : "0 4px",
          }}
        >
          <button
            type="button"
            className="transfer-btn"
            style={s.transferBtn}
            disabled={!selectedAvailableCode}
            onClick={() => {
              const role = unassignedRoles.find((r) => r.roleCode === selectedAvailableCode);
              moveRoleToRight(role);
            }}
            title={isMobile ? "Move Selected Role Down to Pipeline" : "Move Selected Role to Right Pipeline (>)"}
          >
            {isMobile ? (
              <ChevronDown size={22} color="#ffffff" />
            ) : (
              <ChevronRight size={22} color="#ffffff" />
            )}
          </button>

          <button
            type="button"
            className="transfer-btn"
            style={s.transferBtn}
            disabled={!selectedAssignedCode}
            onClick={() => {
              const role = assignedRoles.find((r) => r.roleCode === selectedAssignedCode);
              moveRoleToLeft(role);
            }}
            title={isMobile ? "Move Selected Role Up to Available List" : "Move Selected Role Back to Left Pool (<)"}
          >
            {isMobile ? (
              <ChevronUp size={22} color="#ffffff" />
            ) : (
              <ChevronLeft size={22} color="#ffffff" />
            )}
          </button>
        </div>

        {/* RIGHT BOX: ACTIVE WORKFLOW PRIORITY PIPELINE */}
        <div style={s.panelCard}>
          {refreshing && (
            <div style={s.overlayLoader}>
              <div style={s.overlayLoaderPill}>
                <RefreshCw size={16} color="#2563eb" className="spin-icon" />
                <span>Updating pipeline…</span>
              </div>
            </div>
          )}

          <div style={s.panelHeader}>
            <div>
              <div style={s.panelTitle}>Active Workflow Priority Pipeline</div>
              <div style={s.panelSubtitle}>Active Single Priority Department (Ingestion Pipeline)</div>
            </div>
            <span style={s.panelCountBadgeBlue}>{filteredAssigned.length} Pipeline Department</span>
          </div>

          <div style={s.panelSearchBox}>
            <Search size={14} color="#94a3b8" />
            <input
              type="text"
              placeholder="Search pipeline department..."
              value={rightSearchQuery}
              onChange={(e) => setRightSearchQuery(e.target.value)}
              style={s.panelSearchInput}
            />
          </div>

          <div style={s.panelBodyScroll}>
            {loading ? (
              <div style={{ display: "flex", flexDirection: "column", gap: "10px", width: "100%" }}>
                {Array.from({ length: 5 }).map((_, i) => (
                  <div key={`right-skel-${i}`} style={{ ...s.priorityCard(false, false, false, false, false), opacity: 0.85, padding: "16px" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
                      <div style={{ ...s.skeletonBar, width: "110px", height: "16px" }} />
                      <div style={{ ...s.skeletonBar, width: "70px", height: "18px", borderRadius: "10px" }} />
                    </div>
                    <div style={{ ...s.skeletonBar, width: "160px", height: "12px" }} />
                  </div>
                ))}
              </div>
            ) : filteredAssigned.length === 0 ? (
              <div style={s.emptyBoxText}>
                No active roles in the pipeline. Click (&gt;) from the left box to add roles.
              </div>
            ) : (
              <div style={s.cardsList}>
                {filteredAssigned.map((role, index) => {
                  const roleMappings = roleToMappingsMap[role.roleCode] || [];
                  const isFirst = index === 0;
                  const isLast = index === filteredAssigned.length - 1;
                  const isDragging = draggedIndex === index;
                  const isDragOver = dragOverIndex === index;
                  const isSimActive = simStep === index;
                  const isSelected = selectedAssignedCode === role.roleCode;

                  return (
                    <React.Fragment key={role.roleCode}>
                      <div
                        ref={(el) => (cardRefs.current[index] = el)}
                        draggable={true}
                        onClick={() => setSelectedAssignedCode(role.roleCode)}
                        onDragStart={(e) => handleDragStart(e, index)}
                        onDragOver={(e) => handleDragOver(e, index)}
                        onDragLeave={() => setDragOverIndex(null)}
                        onDrop={(e) => handleDrop(e, index)}
                        style={s.priorityCard(isFirst, isDragging, isDragOver, isSimActive, isSelected)}
                      >
                        {/* LEFT VISUAL ACCENT STRIPE */}
                        <div style={s.cardAccentStripe(isFirst, isSimActive)} />

                        {/* INPUT NODE PORT AT TOP */}
                        <div style={s.nodePortTop(isSimActive)} title="Workflow Priority Ingestion Node">
                          <div style={s.nodeDot(isSimActive)} />
                        </div>

                        {/* CARD TOP BAR: DRAG HANDLE + PRIORITY BADGE */}
                        <div style={s.cardHeaderBar}>
                          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                            <div style={s.dragHandle} title="Drag card to reorder routing priority">
                              <GripVertical size={16} color="#64748b" />
                            </div>
                            <div style={s.priorityBadge(isFirst)}>
                              <span style={s.priorityNumber}>
                                {isFirst ? "Priority #1" : `Priority #${role.priorityOrder}`}
                              </span>
                            </div>
                          </div>
                        </div>

                        {/* CARD BODY: ROLE AVATAR + TITLE + CODE */}
                        <div style={s.cardBody}>
                          <div style={s.roleIconBox(isFirst)}>
                            <Shield size={16} color={isFirst ? "#2563eb" : "#475569"} />
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <h3 style={s.roleTitle}>{role.roleName}</h3>
                            <div style={s.roleCodeBadge}>{role.roleCode}</div>
                          </div>
                        </div>

                        {/* CARD BOTTOM BAR: MAPPED DOC TYPES TOGGLE (LEFT) + REORDER & REMOVE CONTROLS (RIGHT) */}
                        <div style={s.cardBottomRow}>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleExpandDocs(role.roleCode);
                            }}
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: "5px",
                              background: expandedDocTypes[role.roleCode] ? "#eff6ff" : "#f8fafc",
                              border: expandedDocTypes[role.roleCode] ? "1px solid #93c5fd" : "1px solid #e2e8f0",
                              borderRadius: "7px",
                              padding: "4px 9px",
                              fontSize: "10.5px",
                              fontWeight: "700",
                              color: expandedDocTypes[role.roleCode] ? "#1d4ed8" : "#475569",
                              cursor: "pointer",
                              transition: "all 0.15s ease",
                              outline: "none",
                            }}
                            title="Click to view/hide mapped document types"
                          >
                            <FileText size={12} color={expandedDocTypes[role.roleCode] ? "#2563eb" : "#64748b"} />
                            <span>MAPPED DOC TYPES ({roleMappings.length})</span>
                            {expandedDocTypes[role.roleCode] ? (
                              <ChevronUp size={13} color="#2563eb" />
                            ) : (
                              <ChevronDown size={13} color="#64748b" />
                            )}
                          </button>

                          {/* REORDER ARROWS & REMOVE BUTTON AT BOTTOM RIGHT CORNER */}
                          <div style={s.reorderControls}>
                            <button
                              type="button"
                              style={s.arrowBtn(isFirst)}
                              disabled={isFirst}
                              onClick={(e) => {
                                e.stopPropagation();
                                moveCard(index, -1);
                              }}
                              title="Move Up in Priority Queue"
                            >
                              <ChevronUp size={13} color={isFirst ? "#cbd5e1" : "#2563eb"} />
                            </button>
                            <button
                              type="button"
                              style={s.arrowBtn(isLast)}
                              disabled={isLast}
                              onClick={(e) => {
                                e.stopPropagation();
                                moveCard(index, 1);
                              }}
                              title="Move Down in Priority Queue"
                            >
                              <ChevronDown size={13} color={isLast ? "#cbd5e1" : "#2563eb"} />
                            </button>
                            <button
                              type="button"
                              style={s.removeBtn}
                              onClick={(e) => {
                                e.stopPropagation();
                                moveRoleToLeft(role);
                              }}
                              title={isMobile ? "Remove to Available Pool (Up)" : "Remove from Pipeline (<)"}
                            >
                              {isMobile ? <ChevronUp size={12} color="#dc2626" /> : <ChevronLeft size={12} color="#dc2626" />}
                              <span>Remove</span>
                            </button>
                          </div>
                        </div>

                        {/* COLLAPSIBLE EXPANDED SECTION: MAPPED DOCUMENT TYPES (DEFAULT HIDE) */}
                        {expandedDocTypes[role.roleCode] && (
                          <div style={s.cardFooterExpanded}>
                            {roleMappings.length > 0 ? (
                              <div style={s.chipsRow}>
                                {roleMappings.map((m) => (
                                  <div key={m.mappingId} style={s.docChip}>
                                    <FileText size={11} color="#2563eb" />
                                    <span>{m.documentTypeName || m.documentType}</span>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <div style={s.unassignedDocText}>No document types mapped to this role yet.</div>
                            )}
                          </div>
                        )}

                        {/* OUTPUT NODE PORT AT BOTTOM */}
                        <div style={s.nodePortBottom(isSimActive)} title="Workflow Fallback Node">
                          <div style={s.nodeDot(isSimActive)} />
                        </div>
                      </div>

                      {/* MINIMALIST EXECUTIVE NODE PIPELINE CONNECTOR */}
                      {!isLast && (
                        <div style={s.nodeConnectorWrap}>
                          <div style={s.nodeCableLine} />
                          <div style={s.nodeBadgePill}>
                            <GitMerge size={11} color="#475569" />
                            <span>Step {index + 1} Fallback Route ➔ Step {index + 2}</span>
                          </div>
                          <div style={s.nodeCableLine} />
                        </div>
                      )}
                    </React.Fragment>
                  );
                })}
              </div>
            )}
          </div>
        </div>

      </div>

      {/* RESET PRIORITY ORDER CONFIRMATION MODAL */}
      {pendingResetConfirm && (
        <div style={s.modalOverlay}>
          <div style={s.modalBox}>
            <div style={s.modalHeader}>
              <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                <div style={{ background: "#fffbe6", border: "1px solid #ffe58f", padding: "8px", borderRadius: "10px", color: "#d48806", display: "flex" }}>
                  <RotateCcw size={22} />
                </div>
                <div>
                  <h3 style={{ margin: 0, fontSize: "16px", fontWeight: "800", color: "#0f172a" }}>
                    Reset Priority Order
                  </h3>
                  <p style={{ margin: "2px 0 0 0", fontSize: "12px", color: "#64748b" }}>
                    Re-sort active workflow pipeline roles into default alphabetical order
                  </p>
                </div>
              </div>
              <button style={s.closeModalBtn} onClick={() => setPendingResetConfirm(false)}>
                <X size={16} />
              </button>
            </div>

            <div style={s.modalBody}>
              <div style={{ fontSize: "13px", color: "#334155", marginBottom: "12px" }}>
                Are you sure you want to reset the priority execution order of active pipeline roles to default alphabetical order?
              </div>
            </div>

            <div style={s.modalFooter}>
              <button
                type="button"
                style={s.cancelModalBtn}
                onClick={() => setPendingResetConfirm(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                style={s.confirmResetBtn}
                onClick={confirmResetOrder}
              >
                <RotateCcw size={14} />
                Reset Order
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const s = {
  fullLoadingBox: {
    padding: "100px 20px",
    textAlign: "center",
    color: "#475569",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    minHeight: "400px",
    background: "#ffffff",
    borderRadius: "16px",
    border: "1.5px solid #cbd5e1",
    boxShadow: "0 10px 30px rgba(15, 23, 42, 0.04)",
  },
  modalOverlay: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: "rgba(15, 23, 42, 0.6)",
    backdropFilter: "blur(4px)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 999999,
    padding: "20px",
  },
  modalBox: {
    background: "#ffffff",
    borderRadius: "16px",
    width: "100%",
    maxWidth: "500px",
    boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
    border: "1px solid #cbd5e1",
    overflow: "hidden",
  },
  modalHeader: {
    padding: "16px 20px",
    borderBottom: "1px solid #f1f5f9",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    background: "#fafafa",
  },
  closeModalBtn: {
    background: "none",
    border: "none",
    color: "#64748b",
    cursor: "pointer",
    padding: "4px",
    borderRadius: "6px",
    display: "flex",
  },
  modalBody: {
    padding: "20px",
  },
  modalFooter: {
    padding: "14px 20px",
    borderTop: "1px solid #f1f5f9",
    background: "#fafafa",
    display: "flex",
    justifyContent: "flex-end",
    gap: "10px",
  },
  cancelModalBtn: {
    padding: "8px 16px",
    borderRadius: "8px",
    border: "1px solid #cbd5e1",
    background: "#ffffff",
    color: "#475569",
    fontSize: "13px",
    fontWeight: "600",
    cursor: "pointer",
  },
  confirmResetBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    padding: "8px 18px",
    borderRadius: "8px",
    border: "none",
    background: "#2563eb",
    color: "#ffffff",
    fontSize: "13px",
    fontWeight: "700",
    cursor: "pointer",
    boxShadow: "0 4px 12px rgba(37,99,235,0.25)",
  },
  page: {
    padding: "0 clamp(10px, 3vw, 24px) clamp(10px, 3vw, 24px) clamp(10px, 3vw, 24px)",
    boxSizing: "border-box",
    width: "100%",
  },
  header: {
    marginBottom: "20px",
  },
  backLink: {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    fontSize: "13px",
    color: THEME,
    cursor: "pointer",
    fontWeight: "600",
    background: "none",
    border: "none",
    padding: 0,
    marginBottom: 0,
    outline: "none",
  },
  breadcrumb: {
    fontSize: "12px",
    color: "#94a3b8",
    marginBottom: "6px",
  },
  title: {
    margin: 0,
    fontSize: "26px",
    fontWeight: "700",
    color: "#0f172a",
  },
  sub: {
    color: "#64748b",
    marginTop: "5px",
    fontSize: "14px",
  },
  badge11: {
    background: "linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)",
    border: "1px solid #93c5fd",
    color: "#1d4ed8",
    fontSize: "11px",
    fontWeight: "700",
    padding: "3px 8px",
    borderRadius: "12px",
    display: "inline-flex",
    alignItems: "center",
    gap: "5px",
  },
  headerTitleRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    flexWrap: "wrap",
    gap: "12px",
    marginTop: "4px",
  },
  headerActions: {
    display: "flex",
    gap: "8px",
    flexWrap: "wrap",
    alignItems: "center",
    marginLeft: "auto",
  },
  unsavedBadge: {
    fontSize: "11.5px",
    fontWeight: "700",
    color: "#d97706",
    background: "#fffbeb",
    border: "1px solid #fde68a",
    padding: "5px 10px",
    borderRadius: "8px",
  },
  saveBtn: (active) => ({
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    padding: "7px 14px",
    borderRadius: "8px",
    border: "none",
    background: active ? "#2563eb" : "#cbd5e1",
    color: "#fff",
    fontWeight: "700",
    fontSize: "12.5px",
    cursor: active ? "pointer" : "not-allowed",
    boxShadow: active ? "0 4px 14px rgba(37,99,235,0.25)" : "none",
    transition: "all 0.15s ease",
  }),
  refreshBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    padding: "7px 14px",
    borderRadius: "8px",
    border: "1.5px solid #cbd5e1",
    background: "#fff",
    color: "#475569",
    fontWeight: "600",
    fontSize: "12.5px",
    cursor: "pointer",
  },
  statsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
    gap: "12px",
    marginBottom: "20px",
  },
  statCard: {
    background: "#fff",
    border: "1.5px solid #cbd5e1",
    borderRadius: "12px",
    padding: "12px 14px",
    display: "flex",
    alignItems: "center",
    gap: "10px",
    boxShadow: "0 1px 3px rgba(0,0,0,0.02)",
  },
  statIconBox: (color) => ({
    background: `${color}12`,
    padding: "9px",
    borderRadius: "10px",
    display: "flex",
  }),
  statValue: {
    fontSize: "18px",
    fontWeight: "800",
    color: "#0f172a",
  },
  statLabel: {
    fontSize: "11px",
    color: "#64748b",
  },
  toolbar: {
    marginBottom: "20px",
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    flexWrap: "wrap",
    gap: "12px",
  },
  simBtn: (active) => ({
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    padding: "8px 14px",
    borderRadius: "8px",
    border: "none",
    background: active ? "#2563eb" : "linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)",
    color: "#ffffff",
    fontWeight: "700",
    fontSize: "12.5px",
    cursor: active ? "wait" : "pointer",
    boxShadow: "0 2px 8px rgba(37,99,235,0.25)",
    transition: "all 0.15s ease",
  }),
  aiBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    padding: "8px 14px",
    borderRadius: "8px",
    border: "1.5px solid #c084fc",
    background: "#faf5ff",
    color: "#7e22ce",
    fontWeight: "700",
    fontSize: "12.5px",
    cursor: "pointer",
    boxShadow: "0 2px 6px rgba(126,34,206,0.08)",
  },
  resetBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    padding: "8px 14px",
    borderRadius: "8px",
    border: "1.5px solid #cbd5e1",
    background: "#fff",
    color: "#64748b",
    fontWeight: "600",
    fontSize: "12px",
    cursor: "pointer",
  },
  simBanner: {
    background: "#eff6ff",
    border: "1.5px solid #bfdbfe",
    borderRadius: "12px",
    padding: "12px 18px",
    marginBottom: "16px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    boxShadow: "0 4px 14px rgba(37,99,235,0.1)",
  },
  errorAlert: {
    background: "#fef2f2",
    border: "1px solid #fecaca",
    color: "#991b1b",
    padding: "10px 14px",
    borderRadius: "8px",
    fontSize: "13px",
    marginBottom: "16px",
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },

  /* DUAL BOX GRID LAYOUT */
  dualBoxGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 50px 1.25fr",
    gap: "16px",
    alignItems: "start",
    width: "100%",
  },
  panelCard: {
    background: "#ffffff",
    border: "1.5px solid #cbd5e1",
    borderRadius: "16px",
    padding: "18px",
    boxShadow: "0 10px 30px rgba(15, 23, 42, 0.04)",
    display: "flex",
    flexDirection: "column",
    minHeight: "auto",
    position: "relative",
  },
  panelHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: "14px",
    paddingBottom: "10px",
    borderBottom: "1px solid #f1f5f9",
  },
  panelTitle: {
    fontSize: "15px",
    fontWeight: "800",
    color: "#0f172a",
  },
  panelSubtitle: {
    fontSize: "11px",
    color: "#64748b",
    marginTop: "2px",
  },
  panelCountBadge: {
    fontSize: "11px",
    fontWeight: "700",
    color: "#475569",
    background: "#f1f5f9",
    border: "1px solid #e2e8f0",
    padding: "3px 10px",
    borderRadius: "12px",
  },
  panelCountBadgeBlue: {
    fontSize: "11px",
    fontWeight: "700",
    color: "#1d4ed8",
    background: "#eff6ff",
    border: "1px solid #bfdbfe",
    padding: "3px 10px",
    borderRadius: "12px",
  },
  panelSearchBox: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    background: "#f8fafc",
    border: "1.5px solid #cbd5e1",
    borderRadius: "8px",
    padding: "7px 10px",
    marginBottom: "14px",
  },
  panelSearchInput: {
    border: "none",
    outline: "none",
    background: "transparent",
    width: "100%",
    fontSize: "12.5px",
    color: "#0f172a",
  },
  panelBodyScroll: {
    maxHeight: "650px",
    overflowY: "auto",
    paddingRight: "4px",
  },

  /* AVAILABLE ROLE ITEM (LEFT BOX) */
  availableRoleItem: (isSelected) => ({
    display: "flex",
    alignItems: "center",
    gap: "12px",
    padding: "12px 14px",
    borderRadius: "12px",
    background: isSelected ? "#eff6ff" : "#ffffff",
    border: isSelected ? "2px solid #2563eb" : "1px solid #e2e8f0",
    marginBottom: "10px",
    cursor: "pointer",
    transition: "all 0.15s ease",
    boxShadow: isSelected ? "0 2px 8px rgba(37,99,235,0.15)" : "none",
  }),
  availRoleIconBox: {
    background: "#f8fafc",
    border: "1px solid #e2e8f0",
    padding: "8px",
    borderRadius: "10px",
    display: "flex",
  },
  quickAddBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: "5px",
    background: "#eff6ff",
    border: "1px solid #bfdbfe",
    color: "#2563eb",
    fontSize: "12px",
    fontWeight: "700",
    padding: "6px 12px",
    borderRadius: "8px",
    cursor: "pointer",
    flexShrink: 0,
    whiteSpace: "nowrap",
    transition: "all 0.15s ease",
  },

  /* TRANSFER COLUMN ( > & < ) */
  transferColumn: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: "14px",
    paddingTop: "70px",
  },
  transferBtn: {
    width: "44px",
    height: "44px",
    borderRadius: "12px",
    background: "#2563eb",
    border: "none",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    boxShadow: "0 4px 12px rgba(37,99,235,0.25)",
    transition: "all 0.15s ease",
    outline: "none",
  },

  skeletonBar: {
    height: "14px",
    background: "#e2e8f0",
    borderRadius: "4px",
    width: "100px",
    animation: "pulse 1.5s cubic-bezier(0.4, 0, 0.6, 1) infinite",
  },
  overlayLoader: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: "rgba(255, 255, 255, 0.85)",
    backdropFilter: "blur(4px)",
    zIndex: 999,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: "16px",
  },
  overlayLoaderPill: {
    background: "#ffffff",
    border: "1.5px solid #bfdbfe",
    boxShadow: "0 10px 25px rgba(37,99,235,0.2)",
    padding: "10px 20px",
    borderRadius: "30px",
    display: "inline-flex",
    alignItems: "center",
    gap: "10px",
    color: "#1d4ed8",
    fontWeight: "700",
    fontSize: "13px",
  },
  /* CARDS LIST IN RIGHT BOX */
  loadingBox: {
    padding: "40px 20px",
    textAlign: "center",
    color: "#475569",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: "10px",
  },
  emptyBoxText: {
    padding: "40px 20px",
    textAlign: "center",
    color: "#94a3b8",
    fontSize: "13px",
    fontStyle: "italic",
  },
  cardsList: {
    display: "flex",
    flexDirection: "column",
    gap: "0px",
    alignItems: "center",
    width: "100%",
    padding: "6px 4px",
  },
  priorityCard: (isFirst, isDragging, isDragOver, isSimActive, isSelected) => ({
    position: "relative",
    width: "100%",
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    background: isSimActive ? "#f0f6ff" : isSelected ? "#eff6ff" : "#ffffff",
    border: isSimActive
      ? "2px solid #2563eb"
      : isDragOver
        ? "2.5px dashed #2563eb"
        : isSelected
          ? "2px solid #2563eb"
          : "1.5px solid #cbd5e1",
    borderRadius: "12px",
    padding: "10px 14px",
    opacity: isDragging ? 0.45 : 1,
    transform: isDragging ? "scale(0.98)" : "none",
    boxShadow: isSimActive
      ? "0 0 0 3px rgba(37,99,235,0.18), 0 6px 20px rgba(37,99,235,0.15)"
      : isDragging
        ? "0 10px 25px rgba(37,99,235,0.2)"
        : isSelected
          ? "0 4px 14px rgba(37,99,235,0.15)"
          : "0 2px 6px rgba(15,23,42,0.02)",
    transition: "all 0.2s ease",
    cursor: "grab",
    userSelect: "none",
    boxSizing: "border-box",
  }),
  nodePortTop: (isSimActive) => ({
    position: "absolute",
    top: "-6px",
    left: "50%",
    transform: "translateX(-50%)",
    width: "10px",
    height: "10px",
    borderRadius: "50%",
    background: isSimActive ? "#2563eb" : "#ffffff",
    border: "2px solid #2563eb",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    boxShadow: isSimActive ? "0 0 10px rgba(37,99,235,0.6)" : "0 0 4px rgba(0,0,0,0.1)",
    zIndex: 1,
  }),
  nodePortBottom: (isSimActive) => ({
    position: "absolute",
    bottom: "-6px",
    left: "50%",
    transform: "translateX(-50%)",
    width: "10px",
    height: "10px",
    borderRadius: "50%",
    background: isSimActive ? "#2563eb" : "#ffffff",
    border: "2px solid #2563eb",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    boxShadow: isSimActive ? "0 0 10px rgba(37,99,235,0.6)" : "0 0 4px rgba(0,0,0,0.1)",
    zIndex: 1,
  }),
  nodeDot: (isSimActive) => ({
    width: "3px",
    height: "3px",
    borderRadius: "50%",
    background: isSimActive ? "#ffffff" : "#2563eb",
  }),
  nodeConnectorWrap: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: "2px 0",
    width: "100%",
  },
  nodeCableLine: {
    width: "2px",
    height: "6px",
    background: "linear-gradient(180deg, #cbd5e1 0%, #94a3b8 100%)",
  },
  nodeBadgePill: {
    display: "inline-flex",
    alignItems: "center",
    gap: "5px",
    padding: "2px 10px",
    borderRadius: "12px",
    background: "#f8fafc",
    border: "1px solid #e2e8f0",
    color: "#475569",
    fontSize: "10px",
    fontWeight: "600",
    letterSpacing: "0.2px",
  },
  cardHeaderBar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "10px",
    paddingBottom: "6px",
    borderBottom: "1px solid #f1f5f9",
    marginBottom: "6px",
  },
  dragHandle: {
    cursor: "grab",
    display: "flex",
    alignItems: "center",
    padding: "3px",
    background: "#f8fafc",
    borderRadius: "5px",
    border: "1px solid #e2e8f0",
    transition: "background 0.15s ease",
  },
  cardAccentStripe: (isFirst, isSimActive) => ({
    position: "absolute",
    left: 0,
    top: "10px",
    bottom: "10px",
    width: "4px",
    borderRadius: "0 4px 4px 0",
    background: "linear-gradient(180deg, #2563eb 0%, #3b82f6 100%)",
  }),
  priorityBadge: (isFirst) => ({
    display: "flex",
    alignItems: "center",
    gap: "6px",
    padding: "3px 9px",
    borderRadius: "8px",
    background: "linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)",
    border: "1px solid #93c5fd",
  }),
  priorityNumber: {
    fontSize: "12px",
    fontWeight: "800",
    color: "#1e40af",
  },
  primaryTag: (isFirst) => ({
    fontSize: "10px",
    fontWeight: "700",
    color: "#1d4ed8",
    background: "#bfdbfe",
    padding: "2px 6px",
    borderRadius: "5px",
    letterSpacing: "0.2px",
  }),
  cardBody: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    marginBottom: "8px",
  },
  roleIconBox: (isFirst) => ({
    background: "linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)",
    border: "1.5px solid #93c5fd",
    width: "28px",
    height: "28px",
    borderRadius: "9px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    boxShadow: "0 2px 8px rgba(37,99,235,0.12)",
    flexShrink: 0,
  }),
  roleTitle: {
    margin: 0,
    fontSize: "14px",
    fontWeight: "800",
    color: "#0f172a",
    letterSpacing: "-0.3px",
  },
  roleCodeBadge: {
    fontSize: "10.5px",
    color: "#475569",
    fontFamily: "monospace",
    marginTop: "2px",
    background: "#f1f5f9",
    padding: "1px 6px",
    borderRadius: "5px",
    display: "inline-block",
  },
  cardBottomRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "10px",
    width: "100%",
    marginTop: "2px",
  },
  cardFooterExpanded: {
    background: "#f8fafc",
    borderRadius: "8px",
    padding: "8px 10px",
    border: "1px solid #e2e8f0",
    marginTop: "8px",
    width: "100%",
    boxSizing: "border-box",
  },
  footerLabel: {
    fontSize: "10px",
    fontWeight: "700",
    color: "#64748b",
    letterSpacing: "0.5px",
    marginBottom: "6px",
  },
  chipsRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: "5px",
  },
  docChip: {
    display: "inline-flex",
    alignItems: "center",
    gap: "4px",
    background: "#ffffff",
    border: "1px solid #bfdbfe",
    borderRadius: "6px",
    padding: "2px 7px",
    fontSize: "10.5px",
    fontWeight: "600",
    color: "#1e40af",
  },
  unassignedDocText: {
    fontSize: "10.5px",
    color: "#94a3b8",
    fontStyle: "italic",
  },
  reorderControls: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    marginLeft: "auto",
  },
  arrowBtn: (disabled) => ({
    background: "#ffffff",
    border: "1px solid #cbd5e1",
    borderRadius: "6px",
    padding: "3px 5px",
    cursor: disabled ? "not-allowed" : "pointer",
    opacity: disabled ? 0.3 : 1,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    transition: "all 0.15s ease",
  }),
  removeBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: "4px",
    background: "#fef2f2",
    border: "1px solid #fecaca",
    borderRadius: "6px",
    padding: "3px 7px",
    fontSize: "10.5px",
    fontWeight: "700",
    color: "#dc2626",
    cursor: "pointer",
    transition: "all 0.15s ease",
  },
  toastNotification: (type) => ({
    position: "fixed",
    top: "20px",
    right: "20px",
    zIndex: 99999,
    background: type === "success" ? "#065f46" : type === "info" ? "#1e40af" : "#991b1b",
    color: "#fff",
    padding: "12px 18px",
    borderRadius: "10px",
    fontSize: "13px",
    fontWeight: "600",
    boxShadow: "0 10px 25px rgba(0,0,0,0.2)",
    display: "flex",
    alignItems: "center",
    gap: "8px",
  }),
};
